import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { makeStyles } from "tss-react/mui";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { config_UPTIME } from "../../../Constants";
import { getItem } from "../../../utils/DataProtection";
import { fetchProductTypes } from "../../ProductTypesPage/api";

const REQUIRED_HEADERS = [
  "equipmenttypenodeid",
  "name",
  "equipmentid",
  "inventoryid",
  "constructiondate",
  "manufacturerserialnumber",
];

const REQUIRED_HEADERS_LABEL = [
  "model",
  "name",
  "equipmentid",
  "inventoryid",
  "constructiondate",
  "manufacturerserialnumber",
];

const useStyles = makeStyles()(() => ({
  dropzone: {
    border: "1px dashed rgba(14,63,81,0.32)",
    borderRadius: 8,
    background: "rgba(14,63,81,0.03)",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    textAlign: "center",
    cursor: "pointer",
    transition: "all 120ms ease",
  },
  dropzoneActive: {
    borderColor: "var(--color-primary)",
    background: "rgba(14,63,81,0.06)",
  },
  dropzoneTitle: {
    fontFamily: "Bai Jamjuree, sans-serif",
    fontSize: 18,
    fontWeight: 600,
    lineHeight: "25px",
    letterSpacing: "0.15px",
    color: "var(--color-primary)",
  },
  dropzoneHint: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "rgba(0,0,0,0.6)",
    maxWidth: 520,
  },
  helperText: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0.25px",
    color: "rgba(0,0,0,0.54)",
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  importHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  filenameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  rowsChipWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  tableWrap: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 8,
    overflow: "hidden",
  },
  tableContainer: {
    maxHeight: 420,
    overflowX: "auto",
    overflowY: "auto",
  },
  th: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "16px",
    letterSpacing: "0.4px",
    color: "rgba(0,0,0,0.6)",
    textTransform: "uppercase",
    background: "rgba(0,0,0,0.02)",
  },
  tdPrimary: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "rgba(0,0,0,0.87)",
  },
  tdSecondary: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0.25px",
    color: "rgba(0,0,0,0.6)",
  },
  statusCell: {
    minWidth: 140,
  },
  tableErrorText: {
    fontFamily: "Roboto, sans-serif",
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0.25px",
    color: "#B3261E",
  },
  progressWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  errorsList: {
    margin: 0,
    paddingLeft: 18,
    color: "#B3261E",
    fontFamily: "Roboto, sans-serif",
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0.25px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 16px",
    color: "var(--color-primary)",
    gap: 16,
    flexWrap: "wrap",
  },
  rowsPerPageControl: {
    margin: 0,
    minWidth: 150,
    "& .MuiSelect-select": {
      color: "rgba(0, 0, 0, 0.5)",
      fontFamily: "Roboto, sans-serif",
      fontSize: 16,
      lineHeight: "24px",
      paddingBottom: 4,
    },
  },
}));

function splitCsvLine(line = "") {
  return line.split(";").map((value) => value.trim());
}

function buildRowFromHeaders(headers = [], values = []) {
  return headers.reduce((accumulator, header, valueIndex) => {
    accumulator[header] = values[valueIndex] ?? "";
    return accumulator;
  }, {});
}

function parseLocalizedName(rawValue = "") {
  const parts = String(rawValue)
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);

  if (!parts.length) {
    return "";
  }

  const localizedName = parts.reduce((accumulator, part) => {
    const separatorIndex = part.indexOf(":");

    if (separatorIndex === -1) {
      accumulator.de = part;
      return accumulator;
    }

    const locale = part.slice(0, separatorIndex).trim();
    const value = part.slice(separatorIndex + 1).trim();

    if (locale && value) {
      accumulator[locale] = value;
    }

    return accumulator;
  }, {});

  const locales = Object.keys(localizedName);
  if (locales.length === 0) {
    return rawValue;
  }

  return localizedName;
}

function getDisplayName(nameValue) {
  if (!nameValue) {
    return "—";
  }

  if (typeof nameValue === "string") {
    return nameValue;
  }

  return (
    nameValue.de ||
    nameValue.en ||
    nameValue.fr ||
    Object.values(nameValue)[0] ||
    "—"
  );
}

function parseCsvContent(fileText = "", i18n) {
  const lines = fileText
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return {
      rows: [],
      errors: [i18n.customer_details_csv_import_error_min_rows],
    };
  }

  const headers = splitCsvLine(lines[0]);
  const headerMismatch =
    headers.length !== REQUIRED_HEADERS.length ||
    REQUIRED_HEADERS.some((header, index) => headers[index] !== header);

  if (headerMismatch) {
    return {
      rows: [],
      errors: [
        `${i18n.customer_details_csv_import_error_header} ${REQUIRED_HEADERS_LABEL.join(";")}`,
      ],
    };
  }

  const rows = [];
  const errors = [];

  lines.slice(1).forEach((line, index) => {
    const values = splitCsvLine(line);

    const row = buildRowFromHeaders(headers, values);

    const rowErrors = [];

    if (values.length !== headers.length) {
      rowErrors.push(
        i18n.customer_details_csv_import_error_column_count
          .replace("{row}", index + 2)
          .replace("{count}", headers.length)
      );
    }

    if (!row.equipmenttypenodeid) {
      rowErrors.push(
        i18n.customer_details_csv_import_error_required.replace(
          "{field}",
          "model"
        )
      );
    }

    if (!row.name) {
      rowErrors.push(
        i18n.customer_details_csv_import_error_required.replace("{field}", "name")
      );
    }

    rowErrors.forEach((errorText) => {
      errors.push(
        i18n.customer_details_csv_import_error_row
          .replace("{row}", index + 2)
          .replace("{message}", errorText)
      );
    });

    rows.push({
      rowNumber: index + 2,
      raw: row,
      parsedName: parseLocalizedName(row.name),
      validationErrors: rowErrors,
    });
  });

  return { rows, errors };
}

const getAuthHeaders = () => {
  const userToken = getItem("user");

  return {
    "Content-Type": "application/json",
    ...(userToken ? { Authorization: `Bearer ${JSON.parse(userToken)}` } : {}),
  };
};

async function createEquipmentFromCsvRow(row, equipmentpoolnodeid) {
  const legalentitynodeid = JSON.parse(getItem("legalentitynodeid"));
  const equipmentnodeid = `${config_UPTIME.url.UPTIME_DEFAULT_URL}${uuidv4()}`;


    const payload = {
    lang: "de",
     name: row.parsedName,
    equipmentid: row.raw.equipmentid,
    inventoryid: row.raw.inventoryid,
    constructiondate: Math.floor(Date.UTC(row.raw.constructiondate, 0, 1)),
    manufacturerserialnumber: row.raw.manufacturerserialnumber,
    isFetteDemo: true,
    equipmenttypenodeid: row.resolvedEquipmentTypeNodeId,
    isequipmentof: [
      equipmentpoolnodeid,
      legalentitynodeid,
    ],
    equipmentnodeid,
  };


  const response = await axios({
    method: "post",
    url: `${config_UPTIME.url.API_URL}/equipments`,
    data: payload,
    headers: getAuthHeaders(),
  });

  return response.data;
}

export default function EquipmentCsvImportDialog({
  open,
  onClose,
  equipmentPool,
  onImportSuccess,
  i18n,
}) {
  const { classes, cx } = useStyles();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultSummary, setResultSummary] = useState(null);
  const [rowStatuses, setRowStatuses] = useState({});
  const [typeLookupWarning, setTypeLookupWarning] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const creatableRows = useMemo(
    () =>
      parsedRows.filter(
        (row) => !row.validationErrors?.length && row.resolvedEquipmentTypeNodeId
      ),
    [parsedRows]
  );

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return parsedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [page, parsedRows, rowsPerPage]);

  const canSubmit =
    creatableRows.length > 0 && !isSubmitting;

  const hasImportedPreview =
    Boolean(selectedFileName) || parsedRows.length > 0 || parseErrors.length > 0;

  const resetState = () => {
    setIsDragOver(false);
    setSelectedFileName("");
    setParsedRows([]);
    setParseErrors([]);
    setIsSubmitting(false);
    setProgress(0);
    setResultSummary(null);
    setRowStatuses({});
    setTypeLookupWarning("");
    setPage(0);
    setRowsPerPage(5);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) {
      return;
    }

    const isCsv =
      file.name.toLowerCase().endsWith(".csv") ||
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "";

    if (!isCsv) {
      setSelectedFileName(file.name);
      setParsedRows([]);
      setParseErrors([i18n.customer_details_csv_invalid]);
      setResultSummary(null);
      return;
    }

    const fileText = await file.text();
    const parsed = parseCsvContent(fileText, i18n);
    let equipmentTypeRows = [];

    try {
      equipmentTypeRows = await fetchProductTypes("de");
    } catch (error) {
      equipmentTypeRows = [];
    }

    const equipmentTypeMap = new Map(
      equipmentTypeRows
        .filter((row) => row?.type && row?.id)
        .map((row) => [String(row.type).trim().toLowerCase(), row])
    );

    const hydratedRows = parsed.rows.map((row) => {
      const modelValue = String(row.raw.equipmenttypenodeid || "").trim();
      const matchedType = equipmentTypeMap.get(modelValue.toLowerCase());
      const validationErrors = [...(row.validationErrors || [])];

      if (!matchedType) {
        validationErrors.push(
          i18n.customer_details_csv_import_type_not_found_row.replace(
            "{type}",
            modelValue || "—"
          )
        );
      }

      return {
        ...row,
        validationErrors,
        resolvedEquipmentTypeNodeId: matchedType?.id || "",
        resolvedEquipmentTypeLabel: matchedType?.type || "",
      };
    });

    const hasMissingTypes = hydratedRows.some(
      (row) => !row.resolvedEquipmentTypeNodeId
    );

    setSelectedFileName(file.name);
    setParsedRows(hydratedRows);
    setParseErrors(parsed.errors);
    setResultSummary(null);
    setProgress(0);
    setPage(0);
    setTypeLookupWarning(
      hasMissingTypes ? i18n.customer_details_csv_import_type_not_found_summary : ""
    );
    setRowStatuses(
      hydratedRows.reduce((accumulator, row) => {
        const key = `${row.rowNumber}-${row.raw.equipmentid}-${row.raw.inventoryid}`;
        accumulator[key] = row.validationErrors?.length
          ? {
              status: "error",
              message: row.validationErrors.join(" "),
            }
          : {
              status: "ready",
              message: "",
            };
        return accumulator;
      }, {})
    );
  };

  const handleInputChange = async (event) => {
    const file = event.target.files?.[0];
    await handleFile(file);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    await handleFile(file);
  };

  const handleCreateEquipments = async () => {
    if (!equipmentPool?.nodeid || !canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setProgress(0);
    setResultSummary(null);

    let completed = 0;
    const rowsToCreate = creatableRows;

    const settledResults = await Promise.allSettled(
      rowsToCreate.map((row) =>
        createEquipmentFromCsvRow(row, equipmentPool.nodeid)
          .then((response) => {
            completed += 1;
            setProgress(Math.round((completed / rowsToCreate.length) * 100));
            const key = `${row.rowNumber}-${row.raw.equipmentid}-${row.raw.inventoryid}`;
            setRowStatuses((current) => ({
              ...current,
              [key]: {
                status: "success",
                message: "",
              },
            }));
            return { row, response };
          })
          .catch((error) => {
            completed += 1;
            setProgress(Math.round((completed / rowsToCreate.length) * 100));
            const key = `${row.rowNumber}-${row.raw.equipmentid}-${row.raw.inventoryid}`;
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              i18n.customer_details_csv_import_unknown_error;
            setRowStatuses((current) => ({
              ...current,
              [key]: {
                status: "error",
                message,
              },
            }));
            throw { row, error };
          })
      )
    );

    const successItems = settledResults
      .filter((item) => item.status === "fulfilled")
      .map((item) => item.value);
    const errorItems = settledResults
      .filter((item) => item.status === "rejected")
      .map((item) => item.reason);

    setResultSummary({
      successCount: successItems.length,
      errorCount: errorItems.length,
      errors: errorItems.map(({ row, error }) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          i18n.customer_details_csv_import_unknown_error;

        return i18n.customer_details_csv_import_error_row
          .replace("{row}", row.rowNumber)
          .replace(
            "{message}",
            `${getDisplayName(row.parsedName)}: ${message}`
          );
      }),
    });

    setIsSubmitting(false);

    if (successItems.length > 0) {
      onImportSuccess?.({
        successCount: successItems.length,
        errorCount:
          errorItems.length + (parsedRows.length - rowsToCreate.length),
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const summaryChips = useMemo(() => {
    if (!resultSummary) {
      return [];
    }

    return [
      {
        key: "success",
        color: "success",
        label: `${resultSummary.successCount} ${i18n.customer_details_csv_import_created}`,
      },
      {
        key: "error",
        color: resultSummary.errorCount ? "error" : "default",
        label: `${resultSummary.errorCount} ${i18n.customer_details_csv_import_failed}`,
      },
    ];
  }, [i18n, resultSummary]);

  const getRowStatusMeta = (row) => {
    const key = `${row.rowNumber}-${row.raw.equipmentid}-${row.raw.inventoryid}`;
    const statusEntry = rowStatuses[key];

    if (!statusEntry) {
      return {
        label: i18n.customer_details_csv_import_status_ready,
        color: "default",
        tooltip: "",
      };
    }

    if (statusEntry.status === "success") {
      return {
        label: i18n.customer_details_csv_import_status_success,
        color: "success",
        tooltip: "",
      };
    }

    if (statusEntry.status === "error") {
      return {
        label: i18n.customer_details_csv_import_status_error,
        color: "error",
        tooltip: statusEntry.message || "",
      };
    }

    return {
      label: i18n.customer_details_csv_import_status_ready,
      color: "default",
      tooltip: "",
    };
  };

  const summaryCounts = useMemo(() => {
    const readyCount = parsedRows.filter((row) => {
      const status = getRowStatusMeta(row);
      return status.label === i18n.customer_details_csv_import_status_ready;
    }).length;

    const errorCount = parsedRows.filter((row) => {
      const status = getRowStatusMeta(row);
      return status.label === i18n.customer_details_csv_import_status_error;
    }).length;

    return { readyCount, errorCount };
  }, [parsedRows, rowStatuses, i18n]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      fullScreen
      transitionDuration={500}
      TransitionComponent={Zoom}
      fullWidth
      PaperProps={{
        sx: {
          width: "calc(100vw - 64px)",
          maxWidth: "1800px",
        },
      }}
    >
      <DialogTitle>
        {`${i18n.customer_details_csv_import_title}${equipmentPool?.name ? ` - ${equipmentPool.name}` : ""}`}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.currentTarget === event.target) {
            setIsDragOver(false);
          }
        }}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={handleInputChange}
        />

        {!hasImportedPreview || isDragOver ? (
          <Box
            className={cx(classes.dropzone, isDragOver && classes.dropzoneActive)}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "var(--color-primary)" }} />
            <Typography className={classes.dropzoneTitle}>
              {i18n.customer_details_csv_import_drop_title}
            </Typography>
            <Typography className={classes.dropzoneHint}>
              {i18n.customer_details_csv_import_drop_description}
            </Typography>
            <Typography className={classes.helperText}>
              {REQUIRED_HEADERS_LABEL.join(";")}
            </Typography>
          </Box>
        ) : (
            <div className={classes.importHeader}>
              <div className={classes.summaryRow}>
                <div className={classes.filenameRow}>
                  <InsertDriveFileOutlinedIcon sx={{ color: "rgba(0,0,0,0.54)" }} />
                  <Typography>{selectedFileName}</Typography>
                </div>
                <div className={classes.rowsChipWrap}>
                  <Chip
                    label={`${parsedRows.length} ${i18n.customer_details_csv_import_rows}`}
                    size="small"
                  />
                  <Tooltip
                    title={`${summaryCounts.readyCount} ${i18n.customer_details_csv_import_status_ready}, ${summaryCounts.errorCount} ${i18n.customer_details_csv_import_status_error}`}
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.54)" }} />
                  </Tooltip>
                </div>
              </div>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
              {i18n.customer_details_csv_import_drop_title}
            </Button>
          </div>
        )}

        {/* {parseErrors.length ? (
          <Alert severity="error">
            <ul className={classes.errorsList}>
              {parseErrors.map((errorText) => (
                <li key={errorText}>{errorText}</li>
              ))}
            </ul>
          </Alert>
        ) : null} */}

        {typeLookupWarning ? (
          <Alert severity="warning">{typeLookupWarning}</Alert>
        ) : null}

        {isSubmitting ? (
          <div className={classes.progressWrap}>
            <Typography>{`${i18n.customer_details_csv_import_progress} ${progress}%`}</Typography>
            <LinearProgress variant="determinate" value={progress} />
          </div>
        ) : null}

        {resultSummary ? (
          <>
            <div className={classes.summaryRow}>
              {summaryChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} color={chip.color} size="small" />
              ))}
            </div>
            {resultSummary.errors.length ? (
              <Alert severity="warning">
                <ul className={classes.errorsList}>
                  {resultSummary.errors.map((errorText) => (
                    <li key={errorText}>{errorText}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <Alert severity="success">
                {i18n.customer_details_csv_import_success}
              </Alert>
            )}
          </>
        ) : null}

        {parsedRows.length ? (
          <TableContainer component={Paper} elevation={0} className={classes.tableWrap}>
            <TableContainer className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.th}>Name</TableCell>
                    <TableCell className={classes.th}>Equipment Type</TableCell>
                    <TableCell className={classes.th}>Equipment ID</TableCell>
                    <TableCell className={classes.th}>Inventory ID</TableCell>
                    <TableCell className={classes.th}>Construction Date</TableCell>
                    <TableCell className={classes.th}>Serial Number</TableCell>
                    <TableCell className={classes.th}>
                      {i18n.problem_solution_status}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row) => {
                    const statusMeta = getRowStatusMeta(row);

                    return (
                    <TableRow key={`${row.rowNumber}-${row.raw.equipmentid}-${row.raw.inventoryid}`}>
                      <TableCell>
                        <Typography className={classes.tdPrimary}>
                          {getDisplayName(row.parsedName)}
                        </Typography>
                        {typeof row.parsedName === "object" ? (
                          <Typography className={classes.tdSecondary}>
                            {Object.entries(row.parsedName)
                              .map(([locale, value]) => `${locale}: ${value}`)
                              .join(" | ")}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className={classes.tdSecondary}>
                        {row.raw.equipmenttypenodeid}
                        {row.resolvedEquipmentTypeLabel ? (
                          <Typography className={classes.tdSecondary}>
                            {`${i18n.customer_details_csv_import_type_match}: ${row.resolvedEquipmentTypeLabel}`}
                          </Typography>
                        ) : row.validationErrors?.length ? (
                          <Typography className={classes.tableErrorText}>
                            {row.validationErrors[0]}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className={classes.tdSecondary}>{row.raw.equipmentid || "—"}</TableCell>
                      <TableCell className={classes.tdSecondary}>{row.raw.inventoryid || "—"}</TableCell>
                      <TableCell className={classes.tdSecondary}>
                        {row.raw.constructiondate || "—"}
                      </TableCell>
                      <TableCell className={classes.tdSecondary}>
                        {row.raw.manufacturerserialnumber || "—"}
                      </TableCell>
                      <TableCell className={classes.statusCell}>
                        <Tooltip
                          title={statusMeta.tooltip || ""}
                          arrow
                          disableHoverListener={!statusMeta.tooltip}
                        >
                          <Chip
                            label={statusMeta.label}
                            size="small"
                            color={statusMeta.color}
                            variant={
                              statusMeta.color === "default"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            </TableContainer>
            <div className={classes.footer}>
              <Pagination
                count={Math.max(1, Math.ceil(parsedRows.length / rowsPerPage))}
                page={page + 1}
                onChange={(_, value) => setPage(value - 1)}
                color="text"
                shape="rounded"
              />

              <FormControl variant="standard" className={classes.rowsPerPageControl}>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  label={i18n.rowsPerPage || i18n.rows_per_page || "Rows per page"}
                >
                  {[5, 10, 25].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size} {i18n.rowsPerPage || i18n.rows_per_page || "rows per page"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </TableContainer>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleDialogClose} disabled={isSubmitting}>
          {i18n.customer_details_csv_import_cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateEquipments}
          disabled={!canSubmit}
        >
          {i18n.customer_details_csv_import_create}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
